import React from 'react';

import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Message from 'components/Message';
import { Table } from 'components/Table';
import { ICONS } from 'constants/icons';
import { Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { selectIsMobile } from 'selectors/ui';

import { useItemsReportQuery } from './SkuItemReports.hooks';
import messages from './SkuItemReports.messages';
import './SkuItemReports.scss';

const { block, element } = bem('sku-item-reports');

const EMPTY_VALUE = '—';

const TILES = [
  { key: 'topSeller', label: messages.topSellerTile },
  { key: 'topEarner', label: messages.topEarnerTile },
];

const COLUMNS = [
  { key: 'item', label: messages.itemColumn },
  { key: 'location', label: messages.locationColumn },
  { key: 'menu', label: messages.menuColumn },
  { key: 'category', label: messages.categoryColumn },
  { key: 'unitsSold', label: messages.unitsSoldColumn },
  { key: 'orders', label: messages.ordersColumn },
  { key: 'earnings', label: messages.earningsColumn, align: 'right' },
];

const MOBILE_COLUMNS = [
  { key: 'item', label: messages.itemColumn },
  { key: 'earnings', label: messages.earningsColumn, align: 'right' },
];

const SkuItemReportsPage = () => {
  const intl = useIntl();
  const isMobile = useSelector(selectIsMobile);
  const { data: report } = useItemsReportQuery();

  const items = report?.items || [];
  const totalCount = Number(report?.totalCount) || 0;
  const showEmpty = items.length === 0;

  const columns = isMobile ? MOBILE_COLUMNS : COLUMNS;
  const header = (
    <tr>
      {columns.map(column => (
        <th className={column.align === 'right' ? element('th-right') : undefined} key={column.key}>
          <Message message={column.label} />
        </th>
      ))}
    </tr>
  );

  return (
    <>
      <Message message={messages.pageTitle} as={Title} />

      <div className={block()}>
        <div className={element('tiles')}>
          {TILES.map(tile => (
            <div className={element('tile')} key={tile.key}>
              <span className={element('tile-label')}>
                <Message message={tile.label} />
              </span>
              <span className={element('tile-value')}>{EMPTY_VALUE}</span>
            </div>
          ))}
        </div>

        <div className={element('listing-header')}>
          <span className={element('listing-count')}>
            {intl.formatMessage(messages.itemsCount, { count: totalCount })}
          </span>
        </div>

        <Table className={element('table')} header={header}>
          {showEmpty && [
            <tr key="empty">
              <td colSpan={100}>
                <div className={element('empty')}>
                  <Isvg className={element('empty-icon')} src={ICONS.inbox} />
                  <div className={element('empty-title')}>
                    <Message message={messages.emptyTitle} />
                  </div>
                  <div className={element('empty-body')}>
                    <Message message={messages.emptyBody} />
                  </div>
                </div>
              </td>
            </tr>,
          ]}
        </Table>
      </div>
    </>
  );
};

export default SkuItemReportsPage;
